// token-manager.js — refresh OAuth tokens for the Google + Microsoft
// providers, with one in-flight refresh per accountId.
//
// The refresh secret (client_secret for confidential clients) lives in
// the Supabase oauth-exchange edge function — never on the client. The
// client sends the refresh_token; the function returns a new
// access_token + (optionally) a rotated refresh_token. Token rotation
// matters for Microsoft, which always returns a new refresh_token.

import { supabaseFunctionsBase } from './_supabase-base.js';

// accountId → in-flight Promise<TokenSet>
const _inflight = new Map();

// Proactive-refresh skew window. A token whose `expires_at` is within
// this many ms of "now" is treated as already expired and refreshed
// BEFORE the provider request goes out, rather than after it bounces on a
// 401. 60s absorbs clock drift between client and IDP plus the latency of
// the request itself.
const SKEW_MS = 60000;

// Mockable clock. Tests override this rather than the real wall clock so
// the skew window can be exercised deterministically. The schedule domain
// has no shared time-source module, so the indirection lives here.
let _now = () => Date.now();

/** @returns {Promise<string | null>} the live Supabase JWT, or null. */
async function _supabaseJwt() {
  const vault = (typeof window !== 'undefined' && window.WorkspaceSessionVault) || null;
  if (vault?.getCurrentAccessToken) {
    try {
      return await vault.getCurrentAccessToken();
    } catch {
      return null;
    }
  }
  return null;
}

// refresh({ provider, accountId, credentialKey }: any)
//   → { access_token, refresh_token?, expires_at }
// Loads the stored credentials, hands the refresh_token to the edge
// function, persists the new tokens back to the vault, and returns the
// fresh set. Re-entrant calls for the same accountId collapse onto the
// same promise so the orchestrator never spawns parallel refreshes.
/**
 * @param {any} params - { provider, accountId, credentialKey }
 * @returns {Promise<any>} the refreshed token set, or null.
 */
function refresh({ provider, accountId, credentialKey }: any) {
  if (!accountId) return Promise.resolve(null);
  if (_inflight.has(accountId)) return _inflight.get(accountId);

  const p = (async () => {
    const vault = window.WorkspaceSessionVault;
    if (!vault) throw new Error('vault unavailable');
    const raw = await vault.load(credentialKey);
    if (!raw) throw new Error('no stored credentials');
    const current = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!current.refresh_token) throw new Error('no refresh_token');

    const base = supabaseFunctionsBase();
    if (!base) throw new Error('SUPABASE_URL not configured');
    const jwt = await _supabaseJwt();
    const resp = await fetch(`${base}/oauth-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({
        op: 'refresh',
        provider,
        refresh_token: current.refresh_token,
      }),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`refresh failed: ${resp.status} ${text.slice(0, 200)}`);
    }
    const next = await resp.json();
    // A successful refresh MUST return an access_token. If it doesn't
    // (proxy misbehaving, malformed response, partial IDP outage),
    // throw rather than persisting `undefined` into the vault — that
    // would silently 401 every subsequent provider call with no clue
    // why.
    if (!next || typeof next.access_token !== 'string' || !next.access_token) {
      throw new Error('refresh response missing access_token');
    }
    const merged = {
      access_token: next.access_token,
      refresh_token: next.refresh_token || current.refresh_token,
      expires_at: next.expires_at || Date.now() + (next.expires_in || 3600) * 1000,
      scope: next.scope || current.scope,
      sub: current.sub || next.sub,
    };
    await vault.save(credentialKey, JSON.stringify(merged));
    return merged;
  })();
  _inflight.set(accountId, p);
  // Swallow the rejection on the chained promise so the dangling
  // .finally() handler doesn't trigger an unhandledRejection — the
  // caller still observes the rejection on `p` itself.
  p.catch(() => {}).finally(() => _inflight.delete(accountId));
  return p;
}

// getValidAccessToken({ provider, accountId, credentialKey }: any)
//   → TokenSet { access_token, refresh_token?, expires_at, … } | null
//
// The token LOAD path a provider should call *before* issuing a request.
// It returns a token set guaranteed not to expire within SKEW_MS:
//   • Token fresh (expires_at further out than the skew window, or no
//     expires_at recorded) → returns the stored set as-is, no network.
//   • Token at/inside the skew window → PROACTIVELY refreshes through the
//     existing single-flight `refresh()` and returns the fresh set.
//
// Because the proactive path funnels through the very same `refresh()`
// single-flight `_inflight` map, concurrent callers that all land inside
// the window collapse onto ONE refresh — there is no second refresh path.
// The reactive-on-401 retry in transport.js stays as the fallback for the
// case where a token expires server-side earlier than its advertised
// `expires_at` (revocation, clock skew beyond the window, etc.).
/**
 * @param {any} params - { provider, accountId, credentialKey }
 * @returns {Promise<any>} a token set valid beyond SKEW_MS, or null.
 */
function getValidAccessToken({ provider, accountId, credentialKey }: any) {
  if (!accountId) return Promise.resolve(null);

  // If a refresh for this account is already in flight (e.g. a 401 fallback
  // fired, or another caller already tripped the skew window), join it
  // rather than re-reading the vault and racing a second decision.
  if (_inflight.has(accountId)) return _inflight.get(accountId);

  return (async () => {
    const vault = (typeof window !== 'undefined' && window.WorkspaceSessionVault) || null;
    if (!vault?.load) throw new Error('vault unavailable');
    const raw = await vault.load(credentialKey);
    if (!raw) throw new Error('no stored credentials');
    const current = typeof raw === 'string' ? JSON.parse(raw) : raw;

    const expiresAt = Number(current?.expires_at);
    const fresh = !Number.isFinite(expiresAt) || expiresAt - _now() >= SKEW_MS;
    if (fresh) return current;

    // Inside the skew window → refresh. Re-check the in-flight map: between
    // the await above and here another caller may have started a refresh.
    if (_inflight.has(accountId)) return _inflight.get(accountId);
    return refresh({ provider, accountId, credentialKey });
  })();
}

// Test seam: swap the clock used for the skew-window comparison. Returns
// the previous value so a test can restore it. Passing nothing resets to
// the real wall clock.
/**
 * @param {() => number} [fn] - a clock; omit to reset to Date.now.
 * @returns {() => number} the previous clock.
 */
function _setNow(fn: any) {
  const prev = _now;
  _now = typeof fn === 'function' ? fn : () => Date.now();
  return prev;
}

// Phase 4.7: real ESM. `CalendarTokenManager` is the aggregate api. The
// google/microsoft providers read it off `window.CalendarTokenManager` at
// REQUEST time (not import time) to dodge a provider↔manager cycle, so the
// runtime window mirror stays as a formalized seam.
export const CalendarTokenManager = {
  refresh,
  getValidAccessToken,
  _inflight,
  _setNow,
  SKEW_MS,
};

if (typeof window !== 'undefined') window.CalendarTokenManager = CalendarTokenManager;
